import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Cookie, X } from "@phosphor-icons/react";

/**
 * Lightweight cookie / local-storage disclosure banner.
 *
 * Alaine's Bench uses essential storage only (auth token, UI prefs, the
 * consent flag itself). No third-party analytics. No tracking pixels. So
 * the banner is informational rather than a true Accept/Reject — there's
 * nothing meaningful to decline. One "Got it" acknowledgement and it's gone.
 *
 * Storage key: `alaine_cookie_consent` → ISO timestamp of acknowledgement.
 */
const STORAGE_KEY = "alaine_cookie_consent";

export default function CookieConsent() {
  const [show, setShow] = useState(false);

  useEffect(() => {
    try {
      if (!localStorage.getItem(STORAGE_KEY)) {
        // Tiny delay so the banner animates in after the page settles.
        const t = setTimeout(() => setShow(true), 600);
        return () => clearTimeout(t);
      }
    } catch {
      /* localStorage disabled (private mode) — just don't show */
    }
  }, []);

  const accept = () => {
    try {
      localStorage.setItem(STORAGE_KEY, new Date().toISOString());
    } catch {
      /* ignore */
    }
    setShow(false);
  };

  if (!show) return null;

  return (
    <div
      className="platform-blue fixed bottom-4 left-1/2 -translate-x-1/2 z-[60] w-[calc(100%-2rem)] max-w-2xl"
      data-testid="cookie-consent-banner"
      role="dialog"
      aria-label="Cookie and local storage notice"
    >
      <div className="bg-brand-900 text-stone-50 shadow-xl rounded-lg border border-brand-700 p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center gap-4">
        <div className="flex items-start gap-3 flex-1">
          <div className="h-9 w-9 rounded-md bg-emerald-900/50 text-emerald-300 grid place-items-center shrink-0">
            <Cookie size={18} weight="duotone" />
          </div>
          <div className="text-sm leading-relaxed flex-1">
            <div className="font-semibold text-stone-50">
              Essential cookies only — no tracking.
            </div>
            <div className="text-stone-300 mt-0.5">
              We use local storage to keep you signed in and remember UI
              preferences. No third-party analytics, no ad pixels, no
              behavior tracking. See our{" "}
              <a
                href="/privacy"
                className="underline underline-offset-2 hover:text-white"
                data-testid="cookie-consent-privacy-link"
              >
                Privacy Policy
              </a>{" "}
              for details.
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Button
            onClick={accept}
            className="bg-emerald-700 hover:bg-emerald-600 text-white"
            data-testid="cookie-consent-accept"
          >
            Got it
          </Button>
          <button
            onClick={accept}
            className="text-stone-400 hover:text-white p-1"
            aria-label="Dismiss"
            data-testid="cookie-consent-dismiss"
          >
            <X size={18} />
          </button>
        </div>
      </div>
    </div>
  );
}
